import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { IconLabel } from '@/components/ui/app-icon'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { statsApi } from '@/lib/api'
import { METADATA_KIND_LABELS, metadataByKind } from '@/lib/metadata/catalog'
import { useRenderHistoryStore } from '@/stores/renderHistory.store'
import { elapsedMs, useRenderElapsedTick } from '@/hooks/useRenderElapsedTick'
import { useRenderHistoryHydrated } from '@/hooks/useRenderHistoryHydrated'
import type { ActiveRenderEntry, MetadataKind, MetadataObject, MetadataStatsKey, RenderHistoryEntry, RenderHistorySource } from '@/types/metadata'
import { formatDateTime } from '@/lib/utils'
import { cn } from '@/lib/utils'

type TabId = MetadataKind | 'render_history'

const TABS: { id: TabId; label: string }[] = [
  { id: 'catalog', label: METADATA_KIND_LABELS.catalog },
  { id: 'document', label: METADATA_KIND_LABELS.document },
  { id: 'register', label: METADATA_KIND_LABELS.register },
  { id: 'constant', label: METADATA_KIND_LABELS.constant },
  { id: 'render_history', label: 'История рендера' },
]

const UI_STATUS_LABELS = {
  ready: 'Есть UI',
  schema: 'Только схема',
  planned: 'Планируется',
} as const

const RENDER_SOURCE_LABELS = {
  preview: 'Предпросмотр этикетки',
  task_km: 'Рендер КМ задания',
} as const

function renderSourceLabel(entry: { source: RenderHistorySource; taskId?: string }): string {
  const base = RENDER_SOURCE_LABELS[entry.source]
  if (entry.source === 'task_km' && entry.taskId) {
    return `${base} · ${entry.taskId}`
  }
  return base
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms.toFixed(0)} мс`
  return `${(ms / 1000).toFixed(2)} с`
}

function formatRecords(value: number | undefined): string {
  if (value == null) return '—'
  return value.toLocaleString('ru-RU')
}

function MetadataTable({
  rows,
  stats,
}: {
  rows: MetadataObject[]
  stats?: Partial<Record<MetadataStatsKey, number>>
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-3 font-medium">Название</th>
            <th className="px-4 py-3 font-medium">Описание</th>
            <th className="px-4 py-3 font-medium">Таблица БД</th>
            <th className="px-4 py-3 font-medium">Модуль</th>
            <th className="px-4 py-3 font-medium text-right">Записей</th>
            <th className="px-4 py-3 font-medium">UI</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const count = row.statsKey ? stats?.[row.statsKey] : undefined
            return (
              <tr key={row.id} className="hover:bg-gray-50/80">
                <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{row.name}</td>
                <td className="px-4 py-3 text-gray-600 max-w-md">{row.description}</td>
                <td className="px-4 py-3">
                  <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{row.dbTable}</code>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">{row.module}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                  {formatRecords(count)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <UiStatusCell row={row} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function UiStatusCell({ row }: { row: MetadataObject }) {
  const label = UI_STATUS_LABELS[row.uiStatus]
  const color =
    row.uiStatus === 'ready'
      ? 'bg-green-100 text-green-800'
      : row.uiStatus === 'schema'
        ? 'bg-gray-100 text-gray-600'
        : 'bg-amber-100 text-amber-800'

  if (row.uiRoute && row.uiStatus === 'ready') {
    return (
      <Link to={row.uiRoute} className="text-blue-600 hover:underline text-xs font-medium">
        {label}
      </Link>
    )
  }

  return (
    <span className={cn('inline-block px-2 py-0.5 rounded-full text-xs font-semibold', color)}>
      {label}
    </span>
  )
}

function RenderHistoryTable({
  active,
  entries,
  hydrated,
  onClear,
}: {
  active: ActiveRenderEntry[]
  entries: RenderHistoryEntry[]
  hydrated: boolean
  onClear: () => void
}) {
  const tick = useRenderElapsedTick()
  const hasRows = active.length > 0 || entries.length > 0

  if (!hydrated) {
    return <LoadingSpinner />
  }

  if (!hasRows) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
        Пока нет записей. История появится после предпросмотра этикетки или «Рендер КМ» в задании.
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" onClick={onClear} disabled={entries.length === 0}>
          <Trash2 className="h-4 w-4 mr-1" />
          Очистить завершённые
        </Button>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 font-medium">Время</th>
              <th className="px-4 py-3 font-medium">Источник</th>
              <th className="px-4 py-3 font-medium">Контекст</th>
              <th className="px-4 py-3 font-medium text-right">Длительность</th>
              <th className="px-4 py-3 font-medium">Статус</th>
              <th className="px-4 py-3 font-medium">Детали</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {active.map((entry) => (
              <tr key={entry.id} className="bg-blue-50/40">
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                  {formatDateTime(new Date(entry.startedAt).toISOString())}
                </td>
                <td className="px-4 py-3 text-gray-900">{renderSourceLabel(entry)}</td>
                <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={entry.context}>
                  {entry.context}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold text-blue-700">
                  {formatDuration(elapsedMs(entry.startedAt, tick))}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                    В процессе
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">—</td>
              </tr>
            ))}
            {entries.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50/80">
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(entry.at)}</td>
                <td className="px-4 py-3 text-gray-900">{renderSourceLabel(entry)}</td>
                <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={entry.context}>
                  {entry.context}
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-medium text-gray-900">
                  {formatDuration(entry.durationMs)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      'inline-block px-2 py-0.5 rounded-full text-xs font-semibold',
                      entry.status === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
                    )}
                  >
                    {entry.status === 'ok' ? 'OK' : 'Ошибка'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs max-w-sm truncate" title={entry.detail}>
                  {entry.detail ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function resolveInitialTab(searchParams: URLSearchParams): TabId {
  const tab = searchParams.get('tab')
  if (tab === 'render_history') return 'render_history'
  if (tab === 'catalog' || tab === 'document' || tab === 'register' || tab === 'constant') return tab
  return 'catalog'
}

export function MetadataPage() {
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<TabId>(() => resolveInitialTab(searchParams))
  const historyHydrated = useRenderHistoryHydrated()
  const entries = useRenderHistoryStore((s) => s.entries)
  const active = useRenderHistoryStore((s) => s.active)
  const clearHistory = useRenderHistoryStore((s) => s.clear)

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: async () => (await statsApi.get()).data,
    staleTime: 30_000,
  })

  const rows = useMemo(() => {
    if (tab === 'render_history') return []
    return metadataByKind(tab)
  }, [tab])

  const tabCounts = useMemo(
    () => ({
      catalog: metadataByKind('catalog').length,
      document: metadataByKind('document').length,
      register: metadataByKind('register').length,
      constant: metadataByKind('constant').length,
    }),
    [],
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          <IconLabel icon="fields">Метаданные</IconLabel>
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Карта объектов конфигурации: справочники, документы, регистры и константы. Без просмотра данных —
          только схема и счётчики из <code className="text-xs bg-gray-100 px-1 rounded">/api/stats</code>.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              tab === t.id
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50',
            )}
          >
            {t.label}
            {t.id !== 'render_history' && (
              <span className="ml-1.5 opacity-70">({tabCounts[t.id]})</span>
            )}
            {t.id === 'render_history' && (entries.length + active.length) > 0 && (
              <span className="ml-1.5 opacity-70">({entries.length + active.length})</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'render_history' ? (
        <RenderHistoryTable
          active={active}
          entries={entries}
          hydrated={historyHydrated}
          onClear={clearHistory}
        />
      ) : statsLoading ? (
        <LoadingSpinner />
      ) : (
        <MetadataTable rows={rows} stats={stats} />
      )}
    </div>
  )
}
